import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { media, textSize } from '../../../styles/brand';

import api from '../../../services/api';
import FilterSelect from '../../../components/Selects/FilterSelect';
import { useAuth } from '../../../hooks/auth';
import { useModal } from '../../../hooks/modal';
import { useToast } from '../../../hooks/toast';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import TournamentManageModal from '../../../modals/TournamentManageModal';
import DisqualifyTeamModal from '../../../modals/DisqualifyTeamModal';
import ReinstateTeamModal from '../../../modals/ReinstateTeamModal';
import TournamentRanking from '../../../components/TournamentRanking';
import {
  ActionGrid,
  ActionTile,
  BoardLinkCard,
  IRowAction,
  RowActions,
  ScheduleSlotCard,
  SegmentedControl,
  StatusPill,
} from '../../../components/Tournament';
import PresentationAgenda from './PresentationAgenda';
import { ISegmentOption } from '../../../components/Tournament/SegmentedControl';
import {
  AttentionIcon,
  CalendarIcon,
  CaretRightIcon,
  LiveIcon,
  PinIcon,
  StopwatchIcon,
  TrophyIcon,
  PencilIcon,
  AddUserIcon,
  UserWithAddIcon,
  MiniBotIcon,
  BlobIllustration,
  GridLayoutIcon,
  ArrowBackIcon,
  SearchIcon,
  CrossIcon,
} from '../../../assets/svg/SVGComponents';
import {
  ActionsArea,
  BreakCard,
  CompactNavBar,
  FilterChip,
  FilterChips,
  JudgeOnlyNote,
  CategoryBadge,
  CategoryBadgeRow,
  Container,
  LoadingWrapper,
  MetaGroup,
  MobileSearch,
  SlotCardAction,
  SlotCardActions,
  SlotCardList,
  SlotGroupLabel,
  TabsArea,
  TitleSentinel,
  MainCard,
  MainCardContent,
  ModalityCard,
  ModalityImage,
  ModalityInfo,
  ModalityMeta,
  ModalityName,
  ModalitiesLabel,
  ModalitiesRow,
  NextSlotCard,
  NextSlotLabel,
  NextSlotMeta,
  NextSlotTeam,
  ClearFiltersLink,
  FilterField,
  FilterFieldLabel,
  FilterInput,
  FilterResultCount,
  FilterRow,
  FilterRowTitle,
  PrerequisiteChip,
  PrerequisiteChipRow,
  PrerequisitePill,
  RoundHeaderCell,
  RoundHeaderRow,
  ScheduleEmptyCard,
  ScheduleEmptyDesc,
  ScheduleEmptyTitle,
  ScheduleHeader,
  ScheduleHeaderActions,
  ScheduleSection,
  ScheduleTable,
  ScheduleTableHead,
  ScheduleTableRow,
  ScheduleTableWrapper,
  ScheduleTd,
  ScheduleTh,
  Spinner,
  StatusAndHintsRow,
  TournamentInfo,
  TournamentMeta,
  TournamentTitle,
  TrophyImageWrapper,
  ViewRoundButton,
  ViewResultButton,
  TeamCell,
  VisuallyHidden,
} from './styles';
import {
  CATEGORY_NUMBER_TO_LABEL,
  MODALITY_SLUG_TO_NAME,
} from '../CreateTournament';
import {
  formatWallClockTime,
  formatTournamentDateRange,
} from '../../../utils/scheduleDate';
import { categoryLabel } from '../../../utils/tournament/labels';

interface ITournamentCategory {
  id: string;
  number: number;
  name?: string;
}

interface ITournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  categories?: ITournamentCategory[];
}

interface IScheduleSlotArenaModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
}

interface IScheduleSlotArena {
  id: string;
  name: string;
  tournament_modality: IScheduleSlotArenaModality;
}

interface IScheduleSlotTeam {
  id: string;
  name: string;
  category: string;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface IScheduleSlotJudge {
  id: string;
  name: string;
}

interface IScheduleSlot {
  id: string;
  round_number: number;
  is_break: boolean;
  break_label: string | null;
  starts_at: string;
  ends_at: string;
  status: 'scheduled' | 'ongoing' | 'completed';
  total_score: number | null;
  arena: IScheduleSlotArena;
  team: IScheduleSlotTeam;
  judge: IScheduleSlotJudge | null;
}

interface IScheduleRound {
  id: string;
  team_id: string | null;
  team_name: string;
  arena: string;
  time: string;
  starts_at: string;
  modality: string;
  modality_slug: string;
  tournament_modality_id: string;
  category: string;
  judge: string;
  judge_id: string | null;
  round_number: number;
  status: string;
  max_rounds: number;
  is_break: boolean;
  break_label: string | null;
  total_score: number | null;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface IStandingEntry {
  team: {
    id: string;
    name: string;
    category: string;
    disqualified: boolean;
    disqualification_reason: string | null;
  };
  rounds_total: number;
  presentation_total: number;
  grand_total: number;
  /** Tempo da melhor rodada — critério de desempate do regulamento. */
  best_round_time: number | null;
  rounds_scored: number;
  disqualified: boolean;
}

interface IRankingEntry {
  team_id: string;
  team_name: string;
  category: string;
  total_score: number;
  arena_points: number;
  /** null = apresentação técnica ainda não avaliada. */
  presentation_points: number | null;
  best_round_time: number | null;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface IPresentationStatus {
  team_id: string;
  status: 'scored' | 'pending';
}

type TPrerequisiteKey = 'modalities' | 'arenas' | 'judges' | 'teams';

interface IPrerequisiteCheck {
  key: TPrerequisiteKey;
  satisfied: boolean;
  count: number;
  message?: string;
}

interface ISchedulePrerequisites {
  ready: boolean;
  checks: IPrerequisiteCheck[];
}

interface ITournamentDetails {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  location?: string | null;
  status?: string | null;
  modalities: ITournamentModality[];
  rounds?: IScheduleRound[];
  icon_url?: string | null;
  color?: string | null;
  /** Token do link público do telão; fixo para sempre. */
  display_token?: string;
  stage?: string | null;
  created_by?: string | number | null;
}

interface IJudgeRecord {
  user_id: string;
}

type TDetailsTab = 'schedule' | 'presentations' | 'ranking';

const TAB_LABELS: Record<TDetailsTab, string> = {
  schedule: 'Cronograma',
  presentations: 'Apresentações',
  ranking: 'Classificação',
};

/** A aba ativa fica na URL (`?aba=`): recarregar e voltar mantêm o lugar. */
const TAB_SLUGS: Record<TDetailsTab, string> = {
  schedule: 'cronograma',
  presentations: 'apresentacoes',
  ranking: 'classificacao',
};

const tabFromSlug = (slug: string | null): TDetailsTab | null =>
  (Object.keys(TAB_SLUGS) as TDetailsTab[]).find(
    tab => TAB_SLUGS[tab] === slug,
  ) ?? null;

const MODALITY_IMAGES: Record<string, string> = {
  robo_pintor:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Group+14045%402x.png',
  seguidor_de_linha:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/illustration-seguidor+de+trilha-2x.png',
};

const TROPHY_IMAGE =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/trophy-podium.png';

const PREREQUISITE_PILL_LABELS: Record<TPrerequisiteKey, string> = {
  modalities: 'Modalidades',
  arenas: 'Arenas',
  judges: 'Juízes',
  teams: 'Equipes',
};

const mapSlot = (slot: IScheduleSlot): IScheduleRound => {
  const categoryNum = parseInt(
    slot.team?.category?.replace('category_', '') ?? '',
    10,
  );
  const categoryText =
    CATEGORY_NUMBER_TO_LABEL[categoryNum] ?? categoryLabel(slot.team?.category);
  const modalitySlug = slot.arena?.tournament_modality?.modality ?? '';
  const modalityLabel =
    MODALITY_SLUG_TO_NAME[modalitySlug] ?? modalitySlug ?? '-';
  const timeLabel = slot.starts_at ? formatWallClockTime(slot.starts_at) : '-';
  return {
    id: slot.id,
    team_id: slot.team?.id ?? null,
    team_name: slot.team?.name ?? '-',
    arena: slot.arena?.name ?? '-',
    time: timeLabel,
    starts_at: slot.starts_at,
    modality: modalityLabel,
    modality_slug: modalitySlug,
    tournament_modality_id: slot.arena?.tournament_modality?.id ?? '',
    category: categoryText,
    judge: slot.judge?.name ?? '-',
    judge_id: slot.judge?.id ?? null,
    round_number: slot.round_number ?? 1,
    status:
      slot.total_score !== null ? 'completed' : (slot.status ?? 'scheduled'),
    max_rounds: slot.arena?.tournament_modality?.max_rounds ?? 1,
    is_break: slot.is_break ?? false,
    break_label: slot.break_label ?? null,
    total_score: slot.total_score ?? null,
    disqualified: slot.team?.disqualified ?? false,
    disqualification_reason: slot.team?.disqualification_reason ?? null,
  };
};

const formatRoundDuration = (timeLimitSeconds?: number): string => {
  if (!timeLimitSeconds || timeLimitSeconds <= 0) return '-';
  if (timeLimitSeconds < 60) return `${timeLimitSeconds}s`;
  const minutes = Math.floor(timeLimitSeconds / 60);
  const seconds = timeLimitSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds}s`;
};

const TournamentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleModal } = useModal();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [tournament, setTournament] = useState<ITournamentDetails | null>(null);
  const [rounds, setRounds] = useState<IScheduleRound[]>([]);
  const [standings, setStandings] = useState<IStandingEntry[]>([]);
  // Só juízes cadastrados no torneio avaliam rodadas — em qualquer arena.
  const [judgeIds, setJudgeIds] = useState<string[]>([]);
  const isJudge = judgeIds.includes(String(user?.id));
  const isOrganizer =
    !!tournament && String(tournament.created_by) === String(user?.id);
  const isNational = tournament?.stage === 'national';
  // team_id → banca já avaliou; só carregado para quem pode ver a banca.
  const [presentationStatus, setPresentationStatus] = useState<Record<
    string,
    boolean
  > | null>(null);

  useEffect(() => {
    if (!id || !isNational || !(isJudge || isOrganizer)) {
      setPresentationStatus(null);
      return;
    }
    api
      .get<IPresentationStatus[]>(`/tournaments/${id}/technical-presentations`)
      .then(({ data }) =>
        setPresentationStatus(
          Object.fromEntries(
            data.map(item => [item.team_id, item.status === 'scored']),
          ),
        ),
      )
      .catch(() => setPresentationStatus(null));
  }, [id, isNational, isJudge, isOrganizer, standings]);
  // Agenda da Apresentação Técnica: mesma regra de acesso da banca.
  const canSeePresentations = isNational && (isJudge || isOrganizer);
  const [prerequisites, setPrerequisites] =
    useState<ISchedulePrerequisites | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [filterMyRounds, setFilterMyRounds] = useState(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterMyRounds`) === 'true'
    );
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabInUrl = tabFromSlug(searchParams.get('aba'));
  const [chosenTab, setChosenTab] = useState<TDetailsTab>(
    tabInUrl ?? 'schedule',
  );
  const setActiveTab = useCallback(
    (tab: TDetailsTab) => {
      setChosenTab(tab);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('aba', TAB_SLUGS[tab]);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [filterTeam, setFilterTeam] = useState(() => {
    return localStorage.getItem(`@TournamentFilters:${id}:filterTeam`) || '';
  });
  const [filterModality, setFilterModality] = useState<string | null>(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterModality`) || null
    );
  });
  const [filterCategory, setFilterCategory] = useState<string | null>(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterCategory`) || null
    );
  });

  useEffect(() => {
    localStorage.setItem(
      `@TournamentFilters:${id}:filterMyRounds`,
      String(filterMyRounds),
    );
    localStorage.setItem(`@TournamentFilters:${id}:filterTeam`, filterTeam);
    if (filterModality) {
      localStorage.setItem(
        `@TournamentFilters:${id}:filterModality`,
        filterModality,
      );
    } else {
      localStorage.removeItem(`@TournamentFilters:${id}:filterModality`);
    }
    if (filterCategory) {
      localStorage.setItem(
        `@TournamentFilters:${id}:filterCategory`,
        filterCategory,
      );
    } else {
      localStorage.removeItem(`@TournamentFilters:${id}:filterCategory`);
    }
  }, [filterMyRounds, filterTeam, filterModality, filterCategory, id]);
  const [teamInputFocused, setTeamInputFocused] = useState(false);

  const fetchTournament = useCallback(
    async (showLoading = true) => {
      if (!id) return;
      if (showLoading) setIsLoading(true);

      try {
        const [
          { data: tournamentData },
          { data: scheduleData },
          { data: standingsData },
          { data: judgesData },
        ] = await Promise.all([
          api.get(`/tournaments/${id}`),
          api.get<{
            slots: IScheduleSlot[];
            prerequisites?: ISchedulePrerequisites;
          }>(`/tournaments/${id}/schedule`),
          api
            .get<IStandingEntry[]>(`/tournaments/${id}/rounds/standings`)
            .catch(() => ({ data: [] })),
          api
            .get<IJudgeRecord[]>(`/tournaments/${id}/judges`)
            .catch(() => ({ data: [] as IJudgeRecord[] })),
        ]);

        setJudgeIds((judgesData ?? []).map(judge => String(judge.user_id)));

        setTournament(tournamentData);

        const safeStandings = Array.isArray(standingsData) ? standingsData : [];
        setStandings(safeStandings);

        const slots: IScheduleSlot[] = scheduleData?.slots ?? [];

        if (slots.length === 0) {
          setPrerequisites(scheduleData?.prerequisites ?? null);
          setRounds([]);
          return;
        }

        setPrerequisites(null);
        const mappedRounds = slots.map(mapSlot);
        const enrichedRounds = mappedRounds.map(r => {
          const s = safeStandings.find(st => st.team.id === r.team_id);
          return {
            ...r,
            disqualified: s?.disqualified ?? r.disqualified,
            disqualification_reason:
              s?.team?.disqualification_reason ?? r.disqualification_reason,
          };
        });
        setRounds(enrichedRounds);
      } catch {
        addToast({
          type: 'error',
          expression: 'Erro ao carregar',
          description: 'Não foi possível carregar os dados do torneio.',
        });
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [addToast, id],
  );

  const handleGenerateSchedule = useCallback(async () => {
    if (!id || !tournament?.date) return;
    setIsGenerating(true);
    try {
      const { data } = await api.post<{ slots: IScheduleSlot[] }>(
        `/tournaments/${id}/schedule`,
        {
          start_time: tournament.date,
          end_time: tournament.end_date ?? tournament.date,
        },
      );
      const slots = data?.slots ?? [];
      setPrerequisites(null);
      setRounds(slots.map(mapSlot));
      addToast({
        type: 'success',
        expression: 'Cronograma gerado!',
        description: `${slots.length} rodadas criadas com sucesso.`,
      });
    } catch (err: unknown) {
      const apiErrors = (
        err as {
          response?: { data?: { errors?: { key: string; message: string }[] } };
        }
      )?.response?.data?.errors;
      if (apiErrors?.length) {
        addToast({
          type: 'warn',
          expression: 'Pré-requisitos',
          description: apiErrors.map(e => e.message).join(' · '),
        });
      } else {
        addToast({
          type: 'error',
          expression: 'Erro ao gerar',
          description: 'Não foi possível gerar o cronograma. Tente novamente.',
        });
      }
    } finally {
      setIsGenerating(false);
    }
  }, [id, tournament, addToast]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  const nonBreakRounds = useMemo(
    () => rounds.filter(r => !r.is_break),
    [rounds],
  );

  // Rodadas de equipe desclassificada não vão ser jogadas: ficam fora de
  // "próxima rodada" e não impedem o cronograma de contar como concluído.
  const playableRounds = useMemo(
    () => nonBreakRounds.filter(r => !r.disqualified),
    [nonBreakRounds],
  );

  const hasAnyResults = useMemo(
    () => playableRounds.some(r => r.status === 'completed'),
    [playableRounds],
  );

  const allCompleted = useMemo(
    () =>
      playableRounds.length > 0 &&
      playableRounds.every(r => r.status === 'completed'),
    [playableRounds],
  );

  // Torneio concluído abre na classificação. Só na primeira carga: depois o
  // organizador escolhe a aba, e recarregar (ex.: após desclassificar) não muda.
  const tabChosenFor = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !tournament || tournament.id !== id) return;
    if (tabChosenFor.current === tournament.id) return;
    tabChosenFor.current = tournament.id;
    // Aba na URL (link da banca, recarga) vence o padrão.
    if (tabFromSlug(searchParams.get('aba'))) return;
    const finished = tournament.status === 'completed' || allCompleted;
    setChosenTab(finished && hasAnyResults ? 'ranking' : 'schedule');
  }, [id, isLoading, tournament, allCompleted, hasAnyResults, searchParams]);

  const tabOptions = useMemo<ISegmentOption<TDetailsTab>[]>(
    () =>
      (['schedule', 'presentations', 'ranking'] as TDetailsTab[])
        .filter(
          tab =>
            tab === 'schedule' ||
            (tab === 'presentations' && canSeePresentations) ||
            (tab === 'ranking' && hasAnyResults),
        )
        .map(value => ({ value, label: TAB_LABELS[value] })),
    [canSeePresentations, hasAnyResults],
  );

  // Aba que a pessoa não pode ver (ou que ainda não existe) cai em Cronograma.
  const activeTab: TDetailsTab = tabOptions.some(o => o.value === chosenTab)
    ? chosenTab
    : 'schedule';

  const nextSlot = useMemo(() => {
    const ongoingSlot = playableRounds.find(r => r.status === 'ongoing');
    if (ongoingSlot) return { ...ongoingSlot, _isOngoing: true as const };
    const scheduled = playableRounds
      .filter(r => r.status === 'scheduled')
      .sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
      );
    return scheduled[0]
      ? { ...scheduled[0], _isOngoing: false as const }
      : null;
  }, [playableRounds]);

  const isMobile = useMediaQuery({ query: media.md });

  // Barra compacta do celular: aparece quando o nome do torneio sai da tela.
  const titleSentinelRef = useRef<HTMLSpanElement>(null);
  const [titleHidden, setTitleHidden] = useState(false);

  useEffect(() => {
    const sentinel = titleSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) =>
      setTitleHidden(!entry.isIntersecting && entry.boundingClientRect.top < 0),
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [tournament]);

  const rankingByModality = useMemo(() => {
    if (standings.length === 0)
      return {} as Record<
        string,
        {
          modalityLabel: string;
          categoryLabel: string;
          entries: IRankingEntry[];
        }
      >;
    const grouped: Record<
      string,
      { modalityLabel: string; categoryLabel: string; entries: IRankingEntry[] }
    > = {};
    standings.forEach(st => {
      const teamRound = rounds.find(
        r => r.team_id === st.team.id && !r.is_break,
      );
      if (!teamRound) return;

      const key = `${teamRound.tournament_modality_id}_${st.team.category}`;
      if (!grouped[key]) {
        grouped[key] = {
          modalityLabel:
            MODALITY_SLUG_TO_NAME[teamRound.modality_slug] ??
            teamRound.modality_slug,
          categoryLabel: categoryLabel(st.team.category),
          entries: [],
        };
      }
      // Sem a lista da banca (quem não é juiz nem organizador), mostra o total
      // da apresentação sem distinguir "pendente".
      const presentationScored =
        presentationStatus?.[st.team.id] ?? st.presentation_total > 0;
      grouped[key].entries.push({
        team_id: st.team.id,
        team_name: st.team.name,
        category: categoryLabel(st.team.category),
        total_score: st.grand_total,
        arena_points: st.rounds_total,
        presentation_points: presentationScored ? st.presentation_total : null,
        best_round_time: st.best_round_time,
        disqualified: st.disqualified,
        disqualification_reason: st.team.disqualification_reason,
      });
    });
    return grouped;
  }, [standings, rounds, presentationStatus]);

  const modalityOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    rounds.forEach(r => {
      if (!r.is_break && r.modality && !seen.has(r.modality)) {
        seen.add(r.modality);
        opts.push({ label: r.modality, value: r.modality });
      }
    });
    return opts;
  }, [rounds]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    rounds.forEach(r => {
      if (!r.is_break && r.category && !seen.has(r.category)) {
        seen.add(r.category);
        opts.push({ label: r.category, value: r.category });
      }
    });
    return opts;
  }, [rounds]);

  const handleOpenReinstateModal = useCallback(
    (teamId: string, teamName: string) => {
      handleModal(
        <ReinstateTeamModal
          tournamentId={id || ''}
          teamId={teamId}
          teamName={teamName}
          onSuccess={() => fetchTournament(false)}
        />,
      );
    },
    [id, handleModal, fetchTournament],
  );

  const handleOpenDisqualifyModal = useCallback(
    (teamId: string, teamName: string) => {
      handleModal(
        <DisqualifyTeamModal
          tournamentId={id || ''}
          teamId={teamId}
          teamName={teamName}
          onSuccess={() => fetchTournament(false)}
        />,
      );
    },
    [id, handleModal, fetchTournament],
  );

  const handleOpenManageModal = useCallback(
    (mode: 'edit' | 'teams' | 'judges' | 'arenas') => {
      if (!id) return;
      handleModal(
        <TournamentManageModal
          tournamentId={id}
          mode={mode}
          onUpdated={() => fetchTournament(false)}
        />,
      );
    },
    [fetchTournament, handleModal, id],
  );

  const hasActiveFilters =
    filterTeam.trim() !== '' ||
    filterModality !== null ||
    filterCategory !== null;

  const visibleRounds = useMemo(
    () =>
      rounds.filter(r => {
        if (r.is_break) return true;
        if (isJudge && filterMyRounds && r.judge_id !== user.id) return false;
        if (
          filterTeam.trim() !== '' &&
          !r.team_name.toLowerCase().includes(filterTeam.trim().toLowerCase())
        )
          return false;
        if (filterModality !== null && r.modality !== filterModality)
          return false;
        if (filterCategory !== null && r.category !== filterCategory)
          return false;
        return true;
      }),
    [
      rounds,
      filterMyRounds,
      user.id,
      isJudge,
      filterTeam,
      filterModality,
      filterCategory,
    ],
  );

  type TOrganizedItem =
    | { kind: 'round-header'; roundNumber: number; timeLabel: string }
    | { kind: 'slot'; data: IScheduleRound }
    | { kind: 'break'; data: IScheduleRound };

  const organizedSchedule = useMemo((): TOrganizedItem[] => {
    const sorted = [...visibleRounds].sort(
      (a, b) =>
        new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
    const result: TOrganizedItem[] = [];
    let lastRound = -1;
    sorted.forEach(r => {
      if (r.is_break) {
        result.push({ kind: 'break', data: r });
      } else {
        if (r.round_number !== lastRound) {
          lastRound = r.round_number;
          result.push({
            kind: 'round-header',
            roundNumber: r.round_number,
            timeLabel: r.time,
          });
        }
        result.push({ kind: 'slot', data: r });
      }
    });
    return result;
  }, [visibleRounds]);

  const totalCols = 7 + (hasAnyResults ? 1 : 0) + 2;

  /**
   * Ações secundárias de uma equipe, atrás do "⋯" da linha. Desclassificar
   * abre o modal de confirmação; reverter fica no mesmo lugar para o
   * caminho de volta ser fácil de achar.
   */
  const teamActions = (
    teamId: string | null | undefined,
    teamName: string,
    disqualified: boolean,
  ): IRowAction[] => {
    if (!teamId) return [];
    return disqualified
      ? [
          {
            label: 'Reverter desclassificação',
            onSelect: () => handleOpenReinstateModal(teamId, teamName),
          },
        ]
      : [
          {
            label: 'Desclassificar equipe…',
            destructive: true,
            onSelect: () => handleOpenDisqualifyModal(teamId, teamName),
          },
        ];
  };

  /** Ações de uma rodada no card do celular (mesmas regras da tabela). */
  const renderSlotCardActions = (round: IScheduleRound) => {
    const openRound = (suffix = '') =>
      navigate(
        `/tournament/${id}/rounds/${round.id}?round=${round.round_number}${suffix}`,
      );

    let main: React.ReactNode = null;
    if (round.status === 'completed') {
      main = (
        <SlotCardAction
          type="button"
          $tone="ghost"
          onClick={() => openRound('&view=result')}
        >
          {round.total_score !== null
            ? `${round.total_score} pts`
            : 'Resultado'}
          <CaretRightIcon
            aria-hidden
            width="0.8rem"
            stroke="currentColor"
            strokeWidth={2.5}
            fill="none"
          />
        </SlotCardAction>
      );
    } else if (isJudge && !round.disqualified) {
      // Qualquer juiz do torneio avalia qualquer arena.
      main = (
        <SlotCardAction type="button" onClick={() => openRound()}>
          Avaliar
        </SlotCardAction>
      );
    }

    return (
      <SlotCardActions>
        {main}
        <RowActions
          label={`Ações de ${round.team_name}`}
          actions={teamActions(
            round.team_id,
            round.team_name,
            !!round.disqualified,
          )}
        />
      </SlotCardActions>
    );
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container>
        <p>Torneio não encontrado.</p>
      </Container>
    );
  }

  const dateLabel = formatTournamentDateRange(
    tournament.date,
    tournament.end_date,
  );

  return (
    <Container>
      <Link to="/tournaments">
        <ArrowBackIcon
          width="1.33rem"
          stroke="#1E88E5"
          strokeWidth={3}
          fill="none"
        />
        Voltar
      </Link>

      {isMobile && (
        <CompactNavBar $visible={titleHidden} aria-hidden={!titleHidden}>
          <button
            type="button"
            tabIndex={titleHidden ? 0 : -1}
            onClick={() => navigate('/tournaments')}
          >
            <ArrowBackIcon
              width="1.1rem"
              stroke="currentColor"
              strokeWidth={3}
              fill="none"
            />
            Voltar
          </button>
          <strong>{tournament.name}</strong>
        </CompactNavBar>
      )}

      <MainCard>
        <MainCardContent>
          <TrophyImageWrapper>
            <img src={tournament.icon_url ?? TROPHY_IMAGE} alt="Troféu" />
            <BlobIllustration
              width="100%"
              height="100%"
              fill={tournament.color ?? '#e0e0e0'}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </TrophyImageWrapper>

          <TournamentInfo>
            <TournamentTitle>{tournament.name}</TournamentTitle>
            <TitleSentinel ref={titleSentinelRef} aria-hidden />
            {(tournament.status ||
              prerequisites?.checks.some(c => !c.satisfied)) && (
              <StatusAndHintsRow>
                <StatusPill status={tournament.status} size="md" />
                {prerequisites?.checks
                  .filter(c => !c.satisfied)
                  .map(check => (
                    <PrerequisitePill
                      key={check.key}
                      type="button"
                      onClick={() => {
                        if (check.key === 'teams')
                          navigate(`/tournament/${id}/teams`);
                        else if (check.key === 'arenas')
                          handleOpenManageModal('arenas');
                        else if (check.key === 'judges')
                          handleOpenManageModal('judges');
                        else handleOpenManageModal('edit');
                      }}
                    >
                      <AttentionIcon
                        aria-hidden
                        stroke="currentColor"
                        strokeWidth={1.75}
                        fill="none"
                      />
                      Cadastre{' '}
                      {PREREQUISITE_PILL_LABELS[check.key].toLowerCase()}
                    </PrerequisitePill>
                  ))}
              </StatusAndHintsRow>
            )}
            {(dateLabel || tournament.location) && (
              <MetaGroup>
                {dateLabel && (
                  <TournamentMeta>
                    <CalendarIcon
                      aria-hidden
                      stroke="currentColor"
                      strokeWidth={1.75}
                      fill="none"
                    />
                    {dateLabel}
                  </TournamentMeta>
                )}
                {tournament.location && (
                  <TournamentMeta aria-label={`Local: ${tournament.location}`}>
                    <PinIcon
                      aria-hidden
                      stroke="currentColor"
                      strokeWidth={1.75}
                      fill="none"
                    />
                    {tournament.location}
                  </TournamentMeta>
                )}
              </MetaGroup>
            )}

            <ModalitiesLabel>Modalidades:</ModalitiesLabel>
            <ModalitiesRow>
              {tournament.modalities.map(mod => {
                const modalityName =
                  MODALITY_SLUG_TO_NAME[mod.modality] ?? mod.modality;
                const imgSrc = MODALITY_IMAGES[mod.modality];
                const maxRoundsLabel =
                  typeof mod.max_rounds === 'number' ? mod.max_rounds : '-';
                const roundDurationLabel = formatRoundDuration(
                  mod.time_limit_seconds,
                );
                return (
                  <ModalityCard key={mod.id}>
                    {imgSrc && (
                      <ModalityImage src={imgSrc} alt={modalityName} />
                    )}
                    <ModalityInfo>
                      <ModalityName>{modalityName}</ModalityName>
                      <ModalityMeta>{`Rodadas: ${maxRoundsLabel}`}</ModalityMeta>
                      <ModalityMeta>{`Tempo por rodada: ${roundDurationLabel}`}</ModalityMeta>
                      <CategoryBadgeRow>
                        {mod.categories?.map(cat => (
                          <CategoryBadge key={cat.id}>
                            <MiniBotIcon
                              width="0.85rem"
                              strokeWidth={2}
                              stroke="#1e88e1"
                            />
                            {cat.name ??
                              CATEGORY_NUMBER_TO_LABEL[cat.number]?.split(
                                ':',
                              )[0] ??
                              `Categoria ${cat.number}`}
                          </CategoryBadge>
                        ))}
                      </CategoryBadgeRow>
                    </ModalityInfo>
                  </ModalityCard>
                );
              })}
            </ModalitiesRow>

            <ActionsArea>
              <ActionGrid>
                <ActionTile
                  icon={<PencilIcon />}
                  label="Editar"
                  onClick={() => handleOpenManageModal('edit')}
                />
                <ActionTile
                  // GridLayout é 32×32: traço 2.3 equivale ao 1.75 dos de 24.
                  icon={<GridLayoutIcon strokeWidth={2.3} />}
                  label="Arenas"
                  onClick={() => handleOpenManageModal('arenas')}
                />
                <ActionTile
                  icon={<AddUserIcon />}
                  label="Equipes"
                  onClick={() => navigate(`/tournament/${id}/teams`)}
                />
                <ActionTile
                  icon={<UserWithAddIcon />}
                  label="Juízes"
                  onClick={() => handleOpenManageModal('judges')}
                />
              </ActionGrid>
            </ActionsArea>
          </TournamentInfo>

          {tournament.display_token && (
            <BoardLinkCard
              token={tournament.display_token}
              tournamentName={tournament.name}
            />
          )}
        </MainCardContent>
      </MainCard>

      <ScheduleSection>
        <ScheduleHeader>
          <h2>
            {activeTab === 'ranking'
              ? 'Resultado final'
              : activeTab === 'presentations'
                ? 'Apresentação técnica'
                : 'Quadro de horários'}
          </h2>
          <ScheduleHeaderActions>
            {isJudge && !isMobile && activeTab !== 'presentations' && (
              <SecondaryButton
                type="button"
                onClick={() => setFilterMyRounds(prev => !prev)}
                style={
                  filterMyRounds
                    ? { borderColor: '#FF9800', color: '#FF9800' }
                    : undefined
                }
              >
                {filterMyRounds
                  ? 'Ver todas as rodadas'
                  : 'Visualizar minhas rodadas'}
              </SecondaryButton>
            )}
            {!allCompleted && activeTab !== 'presentations' && (
              <PrimaryButton
                type="button"
                onClick={() => navigate(`/tournament/${id}/schedule/edit`)}
              >
                <CalendarIcon
                  width="1rem"
                  stroke="#fff"
                  strokeWidth={2}
                  fill="none"
                />
                Editar Cronograma
              </PrimaryButton>
            )}
          </ScheduleHeaderActions>
        </ScheduleHeader>

        {/* Estado 2: card de próxima / rodada em andamento */}
        {hasAnyResults &&
          !allCompleted &&
          nextSlot &&
          activeTab === 'schedule' && (
            <NextSlotCard>
              <NextSlotLabel>
                {nextSlot._isOngoing ? (
                  <LiveIcon
                    aria-hidden
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                ) : (
                  <StopwatchIcon
                    aria-hidden
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                )}
                {nextSlot._isOngoing ? 'Em andamento' : 'Próxima rodada'}
              </NextSlotLabel>
              <NextSlotTeam>{nextSlot.team_name}</NextSlotTeam>
              <NextSlotMeta>
                {nextSlot.arena}
                {nextSlot.judge !== '-' ? ` · Juiz: ${nextSlot.judge}` : ''}
                {' · '}
                {nextSlot.time}
              </NextSlotMeta>
            </NextSlotCard>
          )}

        {/* Estado 3: abas (no nacional, Apresentações desde o início) */}
        {tabOptions.length > 1 && (
          <TabsArea>
            <SegmentedControl<TDetailsTab>
              aria-label="Visualização do torneio"
              value={activeTab}
              onChange={setActiveTab}
              options={tabOptions}
            />
          </TabsArea>
        )}

        {/* Aba Apresentações (etapa nacional) */}
        {canSeePresentations && activeTab === 'presentations' && id && (
          <PresentationAgenda tournamentId={id} canEdit />
        )}

        {/* Cronograma (estados 1, 2 e aba Cronograma do estado 3) */}
        {(tabOptions.length === 1 || activeTab === 'schedule') && (
          <>
            {rounds.length > 0 && isMobile && (
              <>
                <MobileSearch>
                  <FilterInput $isFocused={teamInputFocused}>
                    <SearchIcon
                      width="1rem"
                      stroke={teamInputFocused ? '#1e88e5' : '#9ca3af'}
                      fill="none"
                      strokeWidth={2}
                    />
                    <input
                      placeholder="Buscar equipe..."
                      aria-label="Buscar equipe"
                      value={filterTeam}
                      onChange={e => setFilterTeam(e.target.value)}
                      onFocus={() => setTeamInputFocused(true)}
                      onBlur={() => setTeamInputFocused(false)}
                    />
                  </FilterInput>
                </MobileSearch>
                <FilterChips role="group" aria-label="Filtros do cronograma">
                  {isJudge && (
                    <FilterChip
                      type="button"
                      $active={filterMyRounds}
                      aria-pressed={filterMyRounds}
                      onClick={() => setFilterMyRounds(prev => !prev)}
                    >
                      Minhas rodadas
                    </FilterChip>
                  )}
                  {modalityOptions.length > 1 &&
                    modalityOptions.map(opt => (
                      <FilterChip
                        key={`modality-${opt.value}`}
                        type="button"
                        $active={filterModality === opt.value}
                        aria-pressed={filterModality === opt.value}
                        onClick={() =>
                          setFilterModality(prev =>
                            prev === opt.value ? null : opt.value,
                          )
                        }
                      >
                        {opt.label}
                      </FilterChip>
                    ))}
                  {categoryOptions.length > 1 &&
                    categoryOptions.map(opt => (
                      <FilterChip
                        key={`category-${opt.value}`}
                        type="button"
                        $active={filterCategory === opt.value}
                        aria-pressed={filterCategory === opt.value}
                        onClick={() =>
                          setFilterCategory(prev =>
                            prev === opt.value ? null : opt.value,
                          )
                        }
                      >
                        {opt.label}
                      </FilterChip>
                    ))}
                  {hasActiveFilters && (
                    <FilterChip
                      type="button"
                      $active={false}
                      onClick={() => {
                        setFilterTeam('');
                        setFilterModality(null);
                        setFilterCategory(null);
                      }}
                    >
                      Limpar filtros
                    </FilterChip>
                  )}
                </FilterChips>
              </>
            )}

            {rounds.length > 0 && !isMobile && (
              <FilterRow>
                <FilterRowTitle>Filtros</FilterRowTitle>
                <FilterField>
                  <FilterFieldLabel>Equipe</FilterFieldLabel>
                  <FilterInput $isFocused={teamInputFocused}>
                    <SearchIcon
                      width="1rem"
                      stroke={teamInputFocused ? '#1e88e5' : '#9ca3af'}
                      fill="none"
                      strokeWidth={2}
                    />
                    <input
                      placeholder="Buscar equipe..."
                      value={filterTeam}
                      onChange={e => setFilterTeam(e.target.value)}
                      onFocus={() => setTeamInputFocused(true)}
                      onBlur={() => setTeamInputFocused(false)}
                    />
                  </FilterInput>
                </FilterField>

                <FilterField>
                  <FilterFieldLabel>Modalidade</FilterFieldLabel>
                  <FilterSelect
                    name="filter-modality"
                    placeholder="Todas"
                    options={modalityOptions}
                    value={
                      filterModality
                        ? { label: filterModality, value: filterModality }
                        : null
                    }
                    onChange={(opt: { label: string; value: string } | null) =>
                      setFilterModality(opt ? opt.value : null)
                    }
                    isClearable
                    isSearchable={false}
                    styles={{
                      control: (
                        base: object,
                        state: { isFocused: boolean },
                      ) => ({
                        ...base,
                        width: '200px',
                        height: '2.75rem',
                        minHeight: '2.75rem',
                        borderRadius: '0.55rem',
                        borderColor: state.isFocused ? '#1e88e5' : '#d1d5db',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused
                          ? '0 0 0 3px rgba(30, 136, 229, 0.1)'
                          : 'none',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#1e88e5' },
                      }),
                      placeholder: (base: object) => ({
                        ...base,
                        fontSize: textSize(0.88),
                        color: '#9ca3af',
                      }),
                      singleValue: (base: object) => ({
                        ...base,
                        fontSize: textSize(0.88),
                        color: '#323232',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                  />
                </FilterField>

                <FilterField>
                  <FilterFieldLabel>Categoria</FilterFieldLabel>
                  <FilterSelect
                    name="filter-category"
                    placeholder="Todas"
                    options={categoryOptions}
                    value={
                      filterCategory
                        ? { label: filterCategory, value: filterCategory }
                        : null
                    }
                    onChange={(opt: { label: string; value: string } | null) =>
                      setFilterCategory(opt ? opt.value : null)
                    }
                    isClearable
                    isSearchable={false}
                    styles={{
                      control: (
                        base: object,
                        state: { isFocused: boolean },
                      ) => ({
                        ...base,
                        width: '200px',
                        height: '2.75rem',
                        minHeight: '2.75rem',
                        borderRadius: '0.55rem',
                        borderColor: state.isFocused ? '#1e88e5' : '#d1d5db',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused
                          ? '0 0 0 3px rgba(30, 136, 229, 0.1)'
                          : 'none',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#1e88e5' },
                      }),
                      placeholder: (base: object) => ({
                        ...base,
                        fontSize: textSize(0.88),
                        color: '#9ca3af',
                      }),
                      singleValue: (base: object) => ({
                        ...base,
                        fontSize: textSize(0.88),
                        color: '#323232',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                  />
                </FilterField>

                {hasActiveFilters && (
                  <>
                    <FilterResultCount>
                      {visibleRounds.filter(r => !r.is_break).length} de{' '}
                      {nonBreakRounds.length} rodadas
                    </FilterResultCount>
                    <ClearFiltersLink
                      type="button"
                      onClick={() => {
                        setFilterTeam('');
                        setFilterModality(null);
                        setFilterCategory(null);
                      }}
                    >
                      <CrossIcon
                        width="0.65rem"
                        stroke="#dc2626"
                        fill="none"
                        strokeWidth={2.5}
                      />
                      Limpar
                    </ClearFiltersLink>
                  </>
                )}
              </FilterRow>
            )}

            {rounds.length === 0 ? (
              <ScheduleEmptyCard>
                {prerequisites?.ready ? (
                  <>
                    <ScheduleEmptyTitle>
                      Tudo pronto! Gere o cronograma para começar.
                    </ScheduleEmptyTitle>
                    <ScheduleEmptyDesc>
                      Equipes, arenas e juízes configurados. O cronograma será
                      gerado automaticamente com base nessas informações.
                    </ScheduleEmptyDesc>
                    {!tournament?.date && (
                      <ScheduleEmptyDesc style={{ color: '#f59e0b' }}>
                        Defina as datas do torneio antes de gerar o cronograma.
                      </ScheduleEmptyDesc>
                    )}
                    <PrimaryButton
                      type="button"
                      loading={isGenerating}
                      disabled={isGenerating || !tournament?.date}
                      onClick={handleGenerateSchedule}
                    >
                      <CalendarIcon
                        width="1rem"
                        stroke="#fff"
                        strokeWidth={2}
                        fill="none"
                      />
                      Gerar Cronograma
                    </PrimaryButton>
                  </>
                ) : prerequisites && !prerequisites.ready ? (
                  <>
                    <ScheduleEmptyTitle>
                      Configure o torneio para gerar o cronograma
                    </ScheduleEmptyTitle>
                    <ScheduleEmptyDesc>
                      Adicione os itens abaixo para liberar a geração automática
                      do cronograma:
                    </ScheduleEmptyDesc>
                    <PrerequisiteChipRow>
                      {prerequisites.checks
                        .filter(c => !c.satisfied)
                        .map(check => (
                          <PrerequisiteChip
                            key={check.key}
                            type="button"
                            onClick={() => {
                              if (check.key === 'teams')
                                navigate(`/tournament/${id}/teams`);
                              else if (check.key === 'arenas')
                                handleOpenManageModal('arenas');
                              else if (check.key === 'judges')
                                handleOpenManageModal('judges');
                              else handleOpenManageModal('edit');
                            }}
                          >
                            + {PREREQUISITE_PILL_LABELS[check.key]}
                          </PrerequisiteChip>
                        ))}
                    </PrerequisiteChipRow>
                    <ScheduleEmptyDesc style={{ fontSize: textSize(0.75) }}>
                      {prerequisites.checks.filter(c => c.satisfied).length}/
                      {prerequisites.checks.length} itens configurados
                    </ScheduleEmptyDesc>
                  </>
                ) : (
                  <ScheduleEmptyDesc>
                    Nenhuma rodada cadastrada ainda.
                  </ScheduleEmptyDesc>
                )}
              </ScheduleEmptyCard>
            ) : isMobile ? (
              <SlotCardList>
                {organizedSchedule.map(item => {
                  if (item.kind === 'round-header') {
                    return (
                      <SlotGroupLabel key={`rh-${item.roundNumber}`}>
                        Rodada {item.roundNumber} · {item.timeLabel}
                      </SlotGroupLabel>
                    );
                  }

                  const round = item.data;

                  if (item.kind === 'break') {
                    return (
                      <BreakCard key={round.id}>
                        {round.break_label ?? 'Intervalo'} · {round.time}
                      </BreakCard>
                    );
                  }

                  return (
                    <ScheduleSlotCard
                      key={round.id}
                      teamName={round.team_name}
                      arena={round.arena}
                      time={round.time}
                      judge={round.judge}
                      modality={round.modality}
                      category={round.category}
                      status={round.status}
                      disqualified={round.disqualified}
                      action={renderSlotCardActions(round)}
                    />
                  );
                })}
              </SlotCardList>
            ) : (
              <ScheduleTableWrapper>
                <ScheduleTable>
                  <ScheduleTableHead>
                    <tr>
                      <ScheduleTh>Equipe</ScheduleTh>
                      <ScheduleTh>Arena</ScheduleTh>
                      <ScheduleTh>Horário</ScheduleTh>
                      <ScheduleTh>Modalidade</ScheduleTh>
                      <ScheduleTh>Categoria</ScheduleTh>
                      <ScheduleTh>Juiz</ScheduleTh>
                      <ScheduleTh>Status</ScheduleTh>
                      {hasAnyResults && <ScheduleTh>Pontuação</ScheduleTh>}
                      <ScheduleTh>Avaliação</ScheduleTh>
                      <ScheduleTh>
                        <VisuallyHidden>Ações</VisuallyHidden>
                      </ScheduleTh>
                    </tr>
                  </ScheduleTableHead>
                  <tbody>
                    {organizedSchedule.map(item => {
                      if (item.kind === 'round-header') {
                        return (
                          <RoundHeaderRow key={`rh-${item.roundNumber}`}>
                            <RoundHeaderCell colSpan={totalCols}>
                              Rodada {item.roundNumber}
                              <span
                                style={{
                                  fontFamily: "'RedHatDisplay-Regular'",
                                  opacity: 0.65,
                                  marginLeft: '0.5rem',
                                  fontSize: textSize(0.75),
                                  textTransform: 'none',
                                  letterSpacing: 0,
                                }}
                              >
                                · {item.timeLabel}
                              </span>
                            </RoundHeaderCell>
                          </RoundHeaderRow>
                        );
                      }

                      const round = item.data;

                      if (item.kind === 'break') {
                        return (
                          <tr key={round.id}>
                            <ScheduleTd
                              colSpan={totalCols}
                              style={{
                                textAlign: 'center',
                                fontStyle: 'italic',
                                color: '#9ca3af',
                                background: '#fafafa',
                                fontSize: textSize(0.85),
                              }}
                            >
                              {round.break_label ?? 'Intervalo'} · {round.time}
                            </ScheduleTd>
                          </tr>
                        );
                      }

                      return (
                        <ScheduleTableRow
                          key={round.id}
                          $disqualified={!!round.disqualified}
                        >
                          <ScheduleTd>
                            <TeamCell $struck={!!round.disqualified}>
                              {round.team_name}
                            </TeamCell>
                          </ScheduleTd>
                          <ScheduleTd>{round.arena}</ScheduleTd>
                          <ScheduleTd>{round.time}</ScheduleTd>
                          <ScheduleTd>{round.modality}</ScheduleTd>
                          <ScheduleTd>{round.category}</ScheduleTd>
                          <ScheduleTd>{round.judge}</ScheduleTd>
                          <ScheduleTd>
                            <StatusPill
                              scope="slot"
                              status={round.status}
                              disqualified={round.disqualified}
                            />
                          </ScheduleTd>
                          {hasAnyResults && (
                            <ScheduleTd>
                              {round.total_score !== null
                                ? `${round.total_score} pts`
                                : '—'}
                            </ScheduleTd>
                          )}
                          <ScheduleTd>
                            {round.status === 'completed' ? (
                              <ViewResultButton
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/tournament/${id}/rounds/${round.id}?round=${round.round_number}&view=result`,
                                  )
                                }
                              >
                                Ver resultado
                              </ViewResultButton>
                            ) : isJudge ? (
                              // Qualquer juiz do torneio avalia qualquer arena:
                              // o juiz escalado no cronograma é só sugestão.
                              <ViewRoundButton
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/tournament/${id}/rounds/${round.id}?round=${round.round_number}`,
                                  )
                                }
                              >
                                Rodada {round.round_number}
                              </ViewRoundButton>
                            ) : (
                              <JudgeOnlyNote title="Só juízes cadastrados no torneio avaliam rodadas">
                                Rodada {round.round_number} · aguardando juiz
                              </JudgeOnlyNote>
                            )}
                          </ScheduleTd>
                          <ScheduleTd>
                            <RowActions
                              label={`Ações de ${round.team_name}`}
                              actions={teamActions(
                                round.team_id,
                                round.team_name,
                                !!round.disqualified,
                              )}
                            />
                          </ScheduleTd>
                        </ScheduleTableRow>
                      );
                    })}
                  </tbody>
                </ScheduleTable>
              </ScheduleTableWrapper>
            )}
          </>
        )}

        {/* Aba Classificação Final (estado 3) */}
        {hasAnyResults && activeTab === 'ranking' && (
          <>
            {Object.entries(rankingByModality).map(
              ([
                key,
                { modalityLabel, categoryLabel: categoryName, entries },
              ]) => (
                <TournamentRanking
                  key={key}
                  title={`${modalityLabel} · ${categoryName}`}
                  icon={
                    <TrophyIcon
                      aria-hidden
                      width="1.4rem"
                      height="1.4rem"
                      stroke="#FF9800"
                      strokeWidth={2}
                      fill="none"
                    />
                  }
                  showCategory
                  showBreakdown={isNational}
                  showTieTime
                  rows={entries.map((entry, i) => ({
                    id: entry.team_id,
                    position: entry.disqualified ? null : i + 1,
                    team_name: entry.team_name,
                    category_label: entry.category,
                    points: entry.total_score,
                    arena_points: entry.arena_points,
                    presentation_points: entry.presentation_points,
                    best_round_time: entry.best_round_time,
                    disqualified: entry.disqualified,
                    disqualification_reason: entry.disqualification_reason,
                  }))}
                  renderActions={row => (
                    <RowActions
                      label={`Ações de ${row.team_name}`}
                      actions={teamActions(
                        row.id,
                        row.team_name,
                        !!row.disqualified,
                      )}
                    />
                  )}
                />
              ),
            )}
          </>
        )}
      </ScheduleSection>
    </Container>
  );
};

export default TournamentDetails;
